<template>
  <div>
    <div
      v-if="!isLoading && !isFinished"
      @click="triggerUpload"
      @drop.prevent="addFiles($event.dataTransfer.files)"
      @dragover.prevent
      @dragenter="dragging = true"
      @dragend="dragging = false"
      @dragleave="dragging = false"
      :class="['upload-container', 'drop-zone', { 'drag-over': dragging }]"
    >
      <slot name="image" />
      <slot name="headline" v-if="!dragging" />
      <p v-else class="headline">
        {{ $t('W2P_UPLOAD_MATERIAL_RELEASE_FILE') }}
      </p>
      <slot name="description" />
      <svg viewBox="0 0 300 100" preserveAspectRatio="none">
        <path
          :class="{ error: isInvalid }"
          d="M0,0 300,0 300,100 0,100z"
          vector-effect="non-scaling-stroke"
        />
      </svg>
      <input
        type="file"
        ref="fileInput"
        @change="addFiles($event.target.files)"
      />
    </div>
    <div
      :class="[
        'upload-container',
        'loading',
        { success: isFinished && !isInvalid }
      ]"
      v-else
    >
      <div class="upload-progress">
        <img
          v-if="uploadFinished"
          src="@/assets/images/file-upload-success.svg"
        />
        <img v-else src="@/assets/images/file-upload.svg" />
        <div class="upload-progress-bar">
          <p class="small">{{ activeFileName }}</p>
          <div style="display:flex;">
            <pn-progress-bar
              :progress="uploadPercentage"
              size="small"
              :color="uploadFinished ? 'success' : ''"
            ></pn-progress-bar>
            <button @click="reset" class="delete-button">
              <pn-icon
                symbol="trash-can"
                color="gray900"
                size="medium"
                solid
              ></pn-icon>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as utils from '@/helpers/utilHelper.js'

export default {
  data: () => ({
    items: [],
    extensions: [],
    dragging: false
  }),
  created() {
    if (this.accepted.length > 0) {
      this.extensions = this.accepted.split('|')
    }
  },
  props: {
    accepted: {
      required: false,
      type: String,
      default: ''
    },
    uploadPercentage: {
      required: false,
      type: Number,
      default: 0
    },
    isLoading: {
      required: false,
      type: Boolean,
      default: false
    },
    isInvalid: {
      required: false,
      type: Boolean,
      default: false
    },
    isFinished: {
      required: false,
      type: Boolean,
      default: false
    },
    activeFileName: {
      required: false,
      type: String,
      default: ''
    },
    maxFilesize: {
      equired: false,
      type: Number,
      default: null
    }
  },
  computed: {
    uploadFinished() {
      return this.uploadPercentage >= 100
    }
  },
  methods: {
    triggerUpload() {
      this.$refs.fileInput.click()
    },
    addFiles(files) {
      this.items = []
      this.dragging = false
      let uploadedFiles = [...files]
      if (!uploadedFiles) {
        return
      }

      uploadedFiles.forEach(file => {
        this.items.push({ file: file, validated: this.validateFile(file) })
      })

      this.$emit('filesAdded', { files: this.items })
    },
    validateFile(file) {
      const validation = [
        this.validateExtension(file),
        this.validateFileSize(file)
      ]

      return !validation.includes(false)
    },
    validateExtension(file) {
      const extension = file.name.split('.').pop()

      if (!this.extensions.length) {
        return true
      }

      return this.extensions.includes(extension)
    },
    validateFileSize(file) {
      if (!this.maxFilesize) {
        return true
      }

      return utils.bytesToMb(file.size) <= this.maxFilesize
    },
    reset() {
      this.items = []
      this.$emit('reset')
    }
  }
}
</script>

<style lang="scss" scoped>
.delete-button {
  padding: 0;
  border: none;
  background: none;

  &:hover {
    cursor: pointer;
  }
}
.upload-container {
  display: flex;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  padding: 2em;
  position: relative;

  &.drop-zone {
    cursor: pointer;
    &.drag-over img {
      opacity: 0.2;
    }

    &.drag-over {
      background: $blue50;
    }

    &:hover {
      background: $blue50;
    }
  }

  &.loading {
    border: 1px solid $blue700;
    background: $blue50;
  }

  &.success {
    border: 1px solid $green700;
    background: $green50;
  }

  .upload-progress {
    max-width: 50%;
    align-self: center;

    .upload-progress-bar {
      p {
        font-weight: bold;
        text-align: left;
      }
    }
  }

  svg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    path {
      fill: none;
      stroke: $blue700;
      stroke-width: 1;
      stroke-dasharray: 0.5em, 0.5em;
    }
  }

  input {
    display: none;
    position: absolute;
  }
}
</style>
